import {IconProp} from '@fortawesome/fontawesome-svg-core';
import RoundButton from 'containers/CompositionFeedback/RoundButton';
import CompositionAlert from 'containers/EditorPage/CompositionAlert';
import useLearningPathNavigation from 'features/useLearningPathNavigation';
import {useLocation, useNavigate} from 'react-router-dom';
import {useMobile, useQueryParam} from 'utils/hooks';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {CompositionDetailExtended} from 'utils/types/compositions';
import styles from './FeedbackHomepage.module.scss';

export type ViewData = {
  path: string;
  label: string;
  icon: IconProp;
  available: boolean;
};

enum EventNameByView {
  'escrita' = 'multigenre_clicked_on_composition',
  'geral' = 'multigenre_clicked_on_general_review',
  'criterios' = 'multigenre_clicked_on_criteria_review',
  'sentimentos' = 'multigenre_clicked_on_review_emotion',
}

export interface FeedbackHomepageProps {
  composition: ImmutableMap<CompositionDetailExtended>;
  viewList: ViewData[];
}

function FeedbackHomepage({
  composition,
  viewList,
}: FeedbackHomepageProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const {isMobile} = useMobile();
  const queryParams = useQueryParam();
  const {trackEvent} = useTrackEvent();
  const isLearningPath = location.search.includes('trilha');
  const learningPathUserInstanceId = queryParams.get('trilha') ?? '';
  const {learningPath, navigateToLearningPath} = useLearningPathNavigation(
    learningPathUserInstanceId,
  );
  const isReviewGridMultigenre =
    composition?.getIn(['instruction_id', 'genre', 'review_grid']) === 61;
  const childId = composition?.get('child_id');
  const hasRewriting = !!isReviewGridMultigenre && !!childId;

  function redirectByView(view: ViewData) {
    const viewName = view.path.split('/').pop() ?? '';

    trackEvent(EventNameByView[viewName], {
      test: {
        test_id: composition?.get('test_id'),
      },
      composition: {
        composition_id: Number(composition?.get('id')),
      },
      review: {
        review_id: Number(composition?.get('reviews')?.first()?.get('id') ?? 0),
      },
      learningPathInstance: {
        learning_path_instance_id: learningPath?.id ?? 0,
        learning_path_status: learningPath?.status ?? '',
      },
    });

    navigate({
      pathname: view.path,
      search: location.search,
    });
  }

  return (
    <section className={styles.wrapper}>
      <menu>
        {hasRewriting && (
          <CompositionAlert
            trigger={!!childId}
            title="Essa é a correção da escrita. Clique aqui para retornar à reescrita."
            closeable={false}
            onClick={() =>
              navigate(
                `/atividades/escrita/${childId}?trilha=${composition.get(
                  'learning_path_user_instance_id',
                )}`,
              )
            }
          />
        )}

        {!hasRewriting && learningPath?.status !== 'expired' && (
          <CompositionAlert
            trigger={
              !!(
                isLearningPath &&
                learningPath?.status !== 'finished' &&
                learningPath?.get_opinion &&
                !learningPath?.viewed_opinion
              )
            }
            title="Você ainda tem um passo para concluir - Clique Aqui"
            closeable={false}
            onClick={() =>
              navigateToLearningPath.nextStep(Number(composition?.get('id')))
            }
          />
        )}

        {viewList.map((view) => (
          <li key={view.path}>
            <RoundButton
              id={view.label}
              icon={view.available ? view.icon : 'lock'}
              variant="tertiary"
              size={isMobile ? 'small' : 'medium'}
              disabled={!view.available}
              onClick={() => redirectByView(view)}
            />
            <label htmlFor={view.label}>{view.label}</label>
          </li>
        ))}
      </menu>
    </section>
  );
}

export default FeedbackHomepage;
